var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { translate } from '@utils/i18n';
import { Button, showErrorToast } from '@ui';
import { InlineSelect } from '@ui/molecules/inputs/inline/InlineSelect';
import Tags from '@components/Tags';
import { BrushawayFooter } from '@ui/molecules/Brushaway';
import { HeaderField } from '@ui/molecules/Header/HeaderField';
import ActivityFeed from '@features/ActivityFeed';
import PhotoGallery from '@features/PhotoGallery';
import api from '@utils/axios';
import useDeficiencyDetail from '../hooks/useDeficiencyDetail';
import useGetResolutionStatus from '../hooks/useGetResolutionStatus';
import { namespace } from './constants';
import DetailInfo from './components/DetailInfo/DetailInfo';
import { EmbeddedDeficiencyDetailHeader, EmbeddedDeficiencyDetailBody } from './components';
import ResolveModal from '../components/ResolveModal/ResolveModal';
import DeleteModal from './components/DeleteModal';
import useDeficiencyActions from './hooks/useDeficiencyActions';
import './components/EmbeddedDeficiencyDetailStyles.scss';
const EMPTY_INCLUDED_PHOTO_IDS = [];
function EmbeddedDeficiencyDetail({ deficiencyId, onClose, deficiencyStatuses = [], tags = [], variant = 'default', proposalId, isLinked = false, onLinkedChange, includedPhotoIds = EMPTY_INCLUDED_PHOTO_IDS }) {
    var _a, _b, _c, _d;
    const { data: deficiency, isLoading, error } = useDeficiencyDetail(String(deficiencyId));
    const { options: resolutionStatuses } = useGetResolutionStatus();
    const { syncWithAnswer, handleReopen, handleSyncWithAnswer, handleDeficiencyStatusChange, handleResolutionStatusChange, handleTagsChange } = useDeficiencyActions(String(deficiencyId), namespace);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [resolveDialogVisible, setResolveDialogVisible] = useState(false);
    const [proposalLinkLoading, setProposalLinkLoading] = useState(false);
    const [localIncludedPhotoIds, setLocalIncludedPhotoIds] = useState(includedPhotoIds);
    const containerRef = useRef(null);
    const [containerEl, setContainerEl] = useState(null);
    useEffect(() => {
        setLocalIncludedPhotoIds((prev) => {
            if (prev.length === includedPhotoIds.length &&
                prev.every((id, index) => id === includedPhotoIds[index])) {
                return prev;
            }
            return includedPhotoIds;
        });
    }, [includedPhotoIds]);
    useEffect(() => {
        setContainerEl(containerRef.current);
    }, []);
    const toggleDeleteDialog = useCallback(() => {
        setDeleteDialogVisible((previousValue) => !previousValue);
    }, []);
    const toggleResolveDialog = useCallback(() => {
        setResolveDialogVisible((previousValue) => !previousValue);
    }, []);
    const handleProposalLinkToggle = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (!proposalId)
            return;
        setProposalLinkLoading(true);
        try {
            if (isLinked) {
                yield api.delete(`/proposals/${proposalId}/unlink_deficiencies`, {
                    data: { deficiency_ids: [deficiencyId] }
                });
                onLinkedChange === null || onLinkedChange === void 0 ? void 0 : onLinkedChange(false);
            }
            else {
                yield api.post(`/proposals/${proposalId}/link_deficiencies`, {
                    deficiency_ids: [deficiencyId]
                });
                onLinkedChange === null || onLinkedChange === void 0 ? void 0 : onLinkedChange(true);
            }
        }
        catch (_a) {
            showErrorToast(translate('error_message_updating', { namespace }));
        }
        finally {
            setProposalLinkLoading(false);
        }
    }), [proposalId, isLinked, deficiencyId, onLinkedChange]);
    const handlePhotoToggle = useCallback((photoId, included) => __awaiter(this, void 0, void 0, function* () {
        if (!proposalId)
            return;
        try {
            if (included) {
                yield api.post(`/proposals/${proposalId}/add_deficiency_photo`, {
                    deficiency_id: deficiencyId,
                    deficiency_photo_id: photoId
                });
                setLocalIncludedPhotoIds((prev) => (prev.includes(photoId) ? prev : [...prev, photoId]));
            }
            else {
                yield api.delete(`/proposals/${proposalId}/remove_deficiency_photo`, {
                    params: { deficiency_id: deficiencyId, deficiency_photo_id: photoId }
                });
                setLocalIncludedPhotoIds((prev) => prev.filter((id) => id !== photoId));
            }
        }
        catch (_a) {
            showErrorToast(translate('error_message_updating', { namespace }));
        }
    }), [proposalId, deficiencyId]);
    const tagsForSelect = useMemo(() => tags.map(({ label, value }) => ({
        label,
        value: Number(value)
    })), [tags]);
    const photoSelection = useMemo(() => {
        if (variant !== 'proposalCompact')
            return undefined;
        return {
            includedIds: localIncludedPhotoIds,
            onToggle: handlePhotoToggle
        };
    }, [variant, localIncludedPhotoIds, handlePhotoToggle]);
    const tabs = useMemo(() => {
        if (!deficiency)
            return [];
        const photosContent = variant === 'proposalCompact' ? (React.createElement(React.Fragment, null,
            React.createElement("p", { className: "embedded-deficiency-detail__proposal-photos-note" }, translate('photos_included_in_proposal', { namespace })),
            React.createElement(PhotoGallery, { deficiencyId: deficiencyId, hideTimeline: true, selection: photoSelection }))) : (React.createElement(PhotoGallery, { deficiencyId: deficiencyId, hideTimeline: true }));
        return [
            {
                id: 'details',
                label: translate('tabs.details', { namespace }),
                content: React.createElement(DetailInfo, { id: String(deficiencyId), fullWidthGeneralInfo: true })
            },
            {
                id: 'activity',
                label: translate('tabs.activity', { namespace }),
                content: (React.createElement(ActivityFeed, { notesEnabled: false, buildingId: deficiency.building.id, deficiencyId: deficiencyId, compactLayout: true }))
            },
            {
                id: 'photos',
                label: translate('tabs.photos', { namespace }),
                content: photosContent
            }
        ];
    }, [deficiency, deficiencyId, variant, photoSelection]);
    const headerFields = deficiency && (React.createElement(React.Fragment, null,
        React.createElement(HeaderField, { label: translate('deficiency_status', { namespace }) },
            React.createElement(InlineSelect, { options: [...deficiencyStatuses].sort((a, b) => a.label.localeCompare(b.label)), value: (_a = deficiency.deficiencyStatus) !== null && _a !== void 0 ? _a : undefined, onChange: (value) => {
                    void handleDeficiencyStatusChange(value);
                } })),
        React.createElement(HeaderField, { label: translate('resolution_status', { namespace }) },
            React.createElement(InlineSelect, { options: resolutionStatuses.map((status) => ({
                    label: status.label,
                    value: status.value.toString()
                })), value: (_c = (_b = deficiency.deficiencyResolutionStatus) === null || _b === void 0 ? void 0 : _b.toString()) !== null && _c !== void 0 ? _c : undefined, onChange: (value) => {
                    void handleResolutionStatusChange(value);
                } })),
        React.createElement(HeaderField, { label: translate('tags', { namespace: 'generic' }) },
            React.createElement(Tags, { showHeader: false, tagsList: deficiency.tagList, attributeName: "tag_list", tagsForSelect: tagsForSelect, onTagsChange: (values) => {
                    void handleTagsChange(values);
                }, appendTo: containerEl }))));
    const headerActions = (React.createElement(Button, { onClick: toggleDeleteDialog },
        React.createElement("i", { className: "fa-light fa-trash-can" }),
        " ",
        translate('delete', { namespace: 'generic' })));
    return (React.createElement("div", { ref: containerRef, "data-testid": "embedded-deficiency-detail", className: "embedded-deficiency-detail" },
        React.createElement(EmbeddedDeficiencyDetailHeader, { deficiencyId: deficiencyId, inspectionId: (_d = deficiency === null || deficiency === void 0 ? void 0 : deficiency.inspectionId) !== null && _d !== void 0 ? _d : null, secondaryActions: headerActions, fields: headerFields }),
        React.createElement(EmbeddedDeficiencyDetailBody, { isLoading: isLoading, hasError: Boolean(error || !deficiency), tabs: tabs }),
        React.createElement(BrushawayFooter, null,
            React.createElement(Button, { onClick: onClose }, translate('close', { namespace: 'generic' })),
            React.createElement(Button, { variant: "secondary", onClick: () => {
                    void handleSyncWithAnswer();
                }, disabled: syncWithAnswer.isLoading },
                React.createElement("i", { className: "fa-light fa-rotate" }),
                " ",
                translate('sync_with_answer', { namespace })),
            (deficiency === null || deficiency === void 0 ? void 0 : deficiency.isResolved) ? (React.createElement(Button, { variant: "primary", onClick: () => {
                    void handleReopen();
                } },
                React.createElement("i", { className: "fa-light fa-rotate-left" }),
                " ",
                translate('reopen', { namespace }))) : (React.createElement(Button, { variant: "primary", onClick: toggleResolveDialog },
                React.createElement("i", { className: "fa-light fa-check" }),
                " ",
                translate('resolve', { namespace }))),
            variant === 'proposalCompact' && (React.createElement(Button, { variant: "primary", onClick: () => {
                    void handleProposalLinkToggle();
                }, disabled: proposalLinkLoading, "aria-busy": proposalLinkLoading }, isLinked
                ? translate('remove_from_proposal', { namespace })
                : translate('add_to_proposal', { namespace })))),
        deficiency && deleteDialogVisible && (React.createElement(DeleteModal, { deficiencyId: String(deficiency.id), toggleDialog: toggleDeleteDialog, redirectPath: null, onDeleted: onClose })),
        resolveDialogVisible && React.createElement(ResolveModal, { toggleDialog: toggleResolveDialog, deficiencyId: String(deficiencyId) })));
}
export default EmbeddedDeficiencyDetail;
